import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, FileText, Star, BarChart3, LogOut, Trash2, Send, CheckCircle, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [stats, setStats] = useState({});
  const [bookings, setBookings] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const getAuthHeader = () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return null;
    }
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardData = async () => {
    try {
      const authHeader = getAuthHeader();
      if (!authHeader) return;

      const [statsRes, bookingsRes, quotesRes, reviewsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/stats`, authHeader),
        axios.get(`${BACKEND_URL}/api/admin/bookings`, authHeader),
        axios.get(`${BACKEND_URL}/api/admin/quotes`, authHeader),
        axios.get(`${BACKEND_URL}/api/admin/reviews`, authHeader)
      ]);

      setStats(statsRes.data);
      setBookings(bookingsRes.data);
      setQuotes(quotesRes.data);
      setReviews(reviewsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
      toast.error('Failed to load dashboard data');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      const authHeader = getAuthHeader();
      await axios.put(`${BACKEND_URL}/api/admin/bookings/${bookingId}`, { status }, authHeader);
      toast.success('Booking status updated');
      fetchDashboardData();
    } catch (error) {
      console.error('Error updating booking:', error);
      toast.error('Failed to update booking');
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;
    
    try {
      const authHeader = getAuthHeader();
      await axios.delete(`${BACKEND_URL}/api/admin/bookings/${bookingId}`, authHeader);
      toast.success('Booking deleted');
      fetchDashboardData();
    } catch (error) {
      console.error('Error deleting booking:', error);
      toast.error('Failed to delete booking');
    }
  };

  const deleteQuote = async (quoteId) => {
    if (!window.confirm('Are you sure you want to delete this quote?')) return;
    
    try {
      const authHeader = getAuthHeader();
      await axios.delete(`${BACKEND_URL}/api/admin/quotes/${quoteId}`, authHeader);
      toast.success('Quote deleted');
      fetchDashboardData();
    } catch (error) {
      console.error('Error deleting quote:', error);
      toast.error('Failed to delete quote');
    }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    
    try {
      const authHeader = getAuthHeader();
      await axios.delete(`${BACKEND_URL}/api/admin/reviews/${reviewId}`, authHeader);
      toast.success('Review deleted');
      fetchDashboardData();
    } catch (error) {
      console.error('Error deleting review:', error);
      toast.error('Failed to delete review');
    }
  };

  const sendReminder = async (bookingId) => {
    try {
      const authHeader = getAuthHeader();
      await axios.post(`${BACKEND_URL}/api/admin/bookings/${bookingId}/reminder`, {}, authHeader);
      toast.success('Reminder sent successfully');
    } catch (error) {
      console.error('Error sending reminder:', error);
      toast.error('Failed to send reminder');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return <div className="admin-loading">Loading dashboard...</div>;
  }

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <div className="admin-header">
        <div className="container">
          <div className="header-content">
            <div className="header-left">
              <img 
                src="https://customer-assets.emergentagent.com/job_3e8f6982-05b2-47e6-848f-33d6ad405bf8/artifacts/zdc1xlcb_capitalelite%20logo.png" 
                alt="Logo" 
                className="admin-logo"
              />
              <h1>Admin Dashboard</h1>
            </div>
            <Button onClick={handleLogout} variant="outline" className="logout-btn">
              <LogOut size={18} />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="container dashboard-container">
        {/* Stats Cards */}
        <div className="stats-grid">
          <Card className="stat-card">
            <CardContent className="stat-content">
              <Calendar className="stat-icon" />
              <div>
                <p className="stat-label">Total Bookings</p>
                <p className="stat-value">{stats.total_bookings || 0}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card">
            <CardContent className="stat-content">
              <Calendar className="stat-icon pending" />
              <div>
                <p className="stat-label">Pending Bookings</p>
                <p className="stat-value">{stats.pending_bookings || 0}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card">
            <CardContent className="stat-content">
              <FileText className="stat-icon" />
              <div>
                <p className="stat-label">Total Quotes</p>
                <p className="stat-value">{stats.total_quotes || 0}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card">
            <CardContent className="stat-content">
              <FileText className="stat-icon pending" />
              <div>
                <p className="stat-label">Pending Quotes</p>
                <p className="stat-value">{stats.pending_quotes || 0}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="bookings" className="dashboard-tabs">
          <TabsList>
            <TabsTrigger value="bookings">
              <Calendar size={18} />
              Bookings
            </TabsTrigger>
            <TabsTrigger value="quotes">
              <FileText size={18} />
              Quotes
            </TabsTrigger>
            <TabsTrigger value="reviews">
              <Star size={18} />
              Reviews
            </TabsTrigger>
          </TabsList>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <Card>
              <CardHeader>
                <CardTitle>Manage Bookings</CardTitle>
              </CardHeader>
              <CardContent>
                {bookings.length === 0 ? (
                  <p className="empty-state">No bookings yet</p>
                ) : (
                  <div className="table-container">
                    {bookings.map((booking) => (
                      <Card key={booking.id} className="item-card">
                        <CardContent className="item-content">
                          <div className="item-header">
                            <div>
                              <h3>{booking.name}</h3>
                              <Badge className={`status-badge status-${booking.status}`}>
                                {booking.status}
                              </Badge>
                            </div>
                            <div className="item-actions">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => sendReminder(booking.id)}
                                title="Send Reminder"
                              >
                                <Send size={16} />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => deleteBooking(booking.id)}
                                className="delete-btn"
                                title="Delete"
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          </div>
                          
                          <div className="item-details">
                            <p><strong>Email:</strong> {booking.email}</p>
                            <p><strong>Phone:</strong> {booking.phone}</p>
                            <p><strong>Location:</strong> {booking.location}</p>
                            <p><strong>Service:</strong> {booking.cleaning_type}</p>
                            <p><strong>Date:</strong> {booking.date}</p>
                            {booking.special_message && (
                              <p><strong>Message:</strong> {booking.special_message}</p>
                            )}
                            <p><strong>Created:</strong> {formatDate(booking.created_at)}</p>
                          </div>

                          <div className="status-actions">
                            <Button
                              size="sm"
                              onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                              disabled={booking.status === 'confirmed'}
                            >
                              <CheckCircle size={16} />
                              Confirm
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateBookingStatus(booking.id, 'completed')}
                              disabled={booking.status === 'completed'}
                            >
                              Complete
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                              disabled={booking.status === 'cancelled'}
                            >
                              <X size={16} />
                              Cancel
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Quotes Tab */}
          <TabsContent value="quotes">
            <Card>
              <CardHeader>
                <CardTitle>Manage Quote Requests</CardTitle>
              </CardHeader>
              <CardContent>
                {quotes.length === 0 ? (
                  <p className="empty-state">No quote requests yet</p>
                ) : (
                  <div className="table-container">
                    {quotes.map((quote) => (
                      <Card key={quote.id} className="item-card">
                        <CardContent className="item-content">
                          <div className="item-header">
                            <div>
                              <h3>{quote.name}</h3>
                              <Badge className={`status-badge status-${quote.status}`}>
                                {quote.status}
                              </Badge>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => deleteQuote(quote.id)}
                              className="delete-btn"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                          
                          <div className="item-details">
                            <p><strong>Email:</strong> {quote.email}</p>
                            <p><strong>Phone:</strong> {quote.phone}</p>
                            <p><strong>Location:</strong> {quote.location}</p>
                            <p><strong>Service:</strong> {quote.cleaning_type}</p>
                            <p><strong>Date:</strong> {quote.date}</p>
                            {quote.special_message && (
                              <p><strong>Message:</strong> {quote.special_message}</p>
                            )}
                            <p><strong>Created:</strong> {formatDate(quote.created_at)}</p>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews">
            <Card>
              <CardHeader>
                <CardTitle>Manage Reviews</CardTitle>
              </CardHeader>
              <CardContent>
                {reviews.length === 0 ? (
                  <p className="empty-state">No reviews yet. Google Reviews will appear here when integrated.</p>
                ) : (
                  <div className="table-container">
                    {reviews.map((review) => (
                      <Card key={review.id} className="item-card">
                        <CardContent className="item-content">
                          <div className="item-header">
                            <div>
                              <h3>{review.author}</h3>
                              <div className="review-stars">
                                {[...Array(review.rating)].map((_, i) => (
                                  <Star key={i} size={16} fill="#FFD700" color="#FFD700" />
                                ))}
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => deleteReview(review.id)}
                              className="delete-btn"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                          
                          <div className="item-details">
                            <p>{review.text}</p>
                            <p><strong>Source:</strong> {review.source}</p>
                            <p><strong>Created:</strong> {formatDate(review.created_at)}</p>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
